import React, { useState } from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';
import PhoneInput from 'react-phone-input-2';
import { FieldTextInput, Form } from '../../../../components';
import { FormattedMessage } from '../../../../util/reactIntl';
import * as validators from '../../../../util/validators';
import { ArrowRightIcon } from '../MerchantIcons';
import { isValidKsaMsisdnInput, normalizeMsisdn } from './merchantContactPhone';
import { getContactPhoneValue } from '../../../../util/contactPhone';
import css from './ContactCta.module.css';

if (typeof window !== 'undefined') {
  require('../../../../styles/react-phone-input-2.css');
}

const PhoneField = ({
  phoneNumber,
  intl,
  onChange,
  onBlur,
  phoneTouched,
  hasPhone,
  isValidPhoneNumber,
}) => (
  <div className={css.phoneWrap}>
    <PhoneInput
      country="sa"
      onlyCountries={['sa']}
      disableDropdown
      value={phoneNumber}
      placeholder={intl.formatMessage({ id: 'SignupForm.phoneNumberLabel' })}
      onChange={onChange}
      onBlur={onBlur}
    />
    {phoneTouched && !hasPhone ? (
      <p className={css.fieldError}>
        <FormattedMessage id="SignupForm.phoneNumberRequired" />
      </p>
    ) : null}
    {phoneTouched && hasPhone && !isValidPhoneNumber ? (
      <p className={css.fieldError}>
        <FormattedMessage id="EditListingDetailsForm.phoneNumberInvalid" />
      </p>
    ) : null}
  </div>
);

/**
 * Merchant landing contact form — same fields & validation as ContactUsForm,
 * styled for the merchant contact card.
 */
const MerchantContactForm = props => {
  const { intl, currentUser, onSubmit, inProgress, arabicLanguage, initialValues } = props;
  const [phoneTouched, setPhoneTouched] = useState(false);
  const isLoggedIn = !!currentUser?.id?.uuid;

  const emailRequired = validators.required(
    intl.formatMessage({ id: 'ConfirmSignupForm.emailRequired' })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'ConfirmSignupForm.emailInvalid' })
  );

  return (
    <FinalForm
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={formRenderProps => {
        const { handleSubmit, invalid, values, form } = formRenderProps;

        const { phoneNumber, mobile_phone_number } = values || {};
        const phoneValue = getContactPhoneValue({ mobile_phone_number, phoneNumber });
        const hasPhone = !!phoneValue;
        const isValidPhoneNumber = hasPhone ? isValidKsaMsisdnInput(phoneValue) : false;

        const submitDisabled =
          invalid || inProgress || !hasPhone || !isValidPhoneNumber;

        const handleChangePhoneInput = (value, country) => {
          const { dialCode = '' } = country;
          const normalizedValue = normalizeMsisdn(value) || value;
          const normalizedWithoutDialCode =
            dialCode && String(normalizedValue).startsWith(String(dialCode))
              ? String(normalizedValue).slice(String(dialCode).length)
              : String(normalizedValue);
          form.change('phoneNumber', '+' + normalizedValue);
          form.change('mobile_phone_number', {
            countryCode: country.countryCode?.toUpperCase(),
            callingCode: dialCode,
            phoneNumber: normalizedWithoutDialCode,
            value: normalizedValue,
          });
        };

        const fieldClass = css.fieldInput;
        const textareaClass = classNames(fieldClass, css.fieldTextarea);

        const phoneField = (
          <PhoneField
            phoneNumber={phoneNumber}
            intl={intl}
            onChange={handleChangePhoneInput}
            onBlur={() => setPhoneTouched(true)}
            phoneTouched={phoneTouched}
            hasPhone={hasPhone}
            isValidPhoneNumber={isValidPhoneNumber}
          />
        );

        return (
          <Form
            onSubmit={handleSubmit}
            className={classNames(css.merchantForm, arabicLanguage && css.arabicDirection)}
          >
            <div className={css.fieldStack}>
              {isLoggedIn ? (
                <>
                  <FieldTextInput
                    name="name"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.contactNamePlaceholder',
                    })}
                    inputRootClass={fieldClass}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ContactUsForm.contactNameRequired' })
                    )}
                  />
                  <FieldTextInput
                    name="company"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.companyPlaceholder',
                    })}
                    inputRootClass={fieldClass}
                  />
                  <FieldTextInput
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.emailPlaceholder',
                    })}
                    inputRootClass={fieldClass}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                  {phoneField}
                  <FieldTextInput
                    name="subject"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.subjectPlaceholder',
                    })}
                    inputRootClass={fieldClass}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ContactUsForm.subjectRequired' })
                    )}
                  />
                  <FieldTextInput
                    name="description"
                    type="textarea"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.descriptionPlaceholder',
                    })}
                    inputRootClass={textareaClass}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ContactUsForm.descriptionRequired' })
                    )}
                  />
                </>
              ) : (
                <>
                  <FieldTextInput
                    name="first_name"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.firstNamePlaceholder',
                    })}
                    inputRootClass={fieldClass}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ContactUsForm.firstNameRequired' })
                    )}
                  />
                  <FieldTextInput
                    name="last_name"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.lastNamePlaceholder',
                    })}
                    inputRootClass={fieldClass}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ContactUsForm.lastNameRequired' })
                    )}
                  />
                  {phoneField}
                  <FieldTextInput
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.emailPlaceholder',
                    })}
                    inputRootClass={fieldClass}
                    validate={validators.composeValidators(emailRequired, emailValid)}
                  />
                  <FieldTextInput
                    name="company"
                    type="text"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.companyPlaceholder',
                    })}
                    inputRootClass={fieldClass}
                  />
                  <FieldTextInput
                    name="city"
                    type="textarea"
                    placeholder={intl.formatMessage({
                      id: 'ContactUsForm.cityPlaceholder',
                    })}
                    inputRootClass={textareaClass}
                    validate={validators.required(
                      intl.formatMessage({ id: 'ContactUsForm.cityRequired' })
                    )}
                  />
                </>
              )}
            </div>

            <button type="submit" className={css.submitButton} disabled={submitDisabled}>
              {inProgress ? (
                <FormattedMessage id="ContactUsForm.submitButtonLabel" />
              ) : (
                <>
                  <FormattedMessage id="ContactUsForm.submitButtonLabel" />
                  <ArrowRightIcon className={css.submitArrow} />
                </>
              )}
            </button>
          </Form>
        );
      }}
    />
  );
};

export default MerchantContactForm;
